/**
 * ScopeSelector Component
 *
 * A dropdown-based scope selector for navigating between major task areas:
 * - Projects: Project-based task management
 * - General Tasks: Standard task board with filters
 *
 * Features:
 * - Clean dropdown interface (replaces bulky tabs)
 * - Icons for visual distinction
 * - Accessible with ARIA labels
 * - Responsive design
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Select } from 'antd';
import {
  ProjectOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  SettingOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import '../../styles/index.scss';

const { Option } = Select;

const SCOPE_CONFIGS = {
  projects: {
    key: 'projects',
    label: 'Projects',
    icon: <ProjectOutlined />,
    route: '/tasks/projects',
    description: 'Project-based task management',
  },
  tasks: {
    key: 'tasks',
    label: 'General Tasks',
    shortLabel: 'Tasks',
    icon: <AppstoreOutlined />,
    route: '/tasks/main/1',
    description: 'Standard task board',
  },
  analytics: {
    key: 'analytics',
    label: 'Analytics',
    shortLabel: 'Analytics',
    icon: <BarChartOutlined />,
    route: '/tasks/analytics',
    description: 'Task metrics and reporting',
  },
  admin: {
    key: 'admin',
    label: 'Admin',
    shortLabel: 'Admin',
    icon: <SettingOutlined />,
    route: '/tasks/admin',
    description: 'SLA configuration and admin settings',
  },
};

const ScopeSelector = ({
  activeScope = 'projects',
  onScopeChange,
  compact = false,
  className = '',
}) => {
  const handleChange = (value) => {
    if (onScopeChange && value !== activeScope) {
      onScopeChange(value, SCOPE_CONFIGS[value]?.route);
    }
  };

  const scopes = Object.values(SCOPE_CONFIGS);
  const activeConfig = SCOPE_CONFIGS[activeScope] || SCOPE_CONFIGS.projects;

  return (
    <div className={`task-scope-selector ${className}`}>
      <Select
        value={activeScope}
        onChange={handleChange}
        className="task-scope-selector__dropdown"
        popupClassName="task-scope-selector__popup"
        suffixIcon={<CaretDownOutlined className="task-scope-selector__caret" />}
        aria-label="Select task scope"
        dropdownMatchSelectWidth={false}
      >
        {scopes.map((scope) => (
          <Option key={scope.key} value={scope.key}>
            <div className="task-scope-selector__option">
              <span className="task-scope-selector__option-icon" aria-hidden="true">
                {scope.icon}
              </span>
              <span className="task-scope-selector__option-label">
                {compact && scope.shortLabel ? scope.shortLabel : scope.label}
              </span>
            </div>
          </Option>
        ))}
      </Select>

      {/* Current scope indicator for screen readers */}
      <span className="sr-only">
        Currently viewing: {activeConfig.label}
      </span>
    </div>
  );
};

ScopeSelector.propTypes = {
  activeScope: PropTypes.oneOf(['projects', 'tasks', 'analytics', 'admin']),
  onScopeChange: PropTypes.func,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default ScopeSelector;
